import useHotkey from '../hooks/use-hotkey';
import { setHotkey } from '@form-engine/builder-application/keybindings';
import useMounted from '@form-engine/core-application/hooks/use-mounted';
import { useCallback } from 'react';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import { CheckCircleFill } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import Builder from '@form-engine/builder-application/index';
import openFile from '@form-engine/builder-application/utilities/open-file';
import { FormL10n, FormL10nSchema } from './export-l10n';
import { getItemById } from '@form-engine/core-util/element-list';
import { isEqual } from 'lodash';

export function applyL10nData(form: FormDefinition, l10nData: FormL10n) {
    const result = structuredClone(form);

    const updatedKeys: string[][] = [];
    const ignoredElements: string[] = [];

    for (const [elementId, elementL10nData] of Object.entries(l10nData)) {
        const element = getItemById(result.elements ?? [], elementId);
        if (!element) {
            ignoredElements.push(elementId);
            console.warn(`l10n import contained unknown element ${elementId}`);
            continue;
        }
        if (!elementL10nData.l10n) {
            ignoredElements.push(elementId);
            console.warn(`l10n import contained empty element ${elementId}`);
            continue;
        }
        for (const [key, l10n] of Object.entries(elementL10nData.l10n)) {
            if ('default' in l10n && !isEqual(element[key], l10n.default)) {
                updatedKeys.push([elementId, 'default', key]);
                element[key] = l10n.default;
            }
            for (const [locale, value] of Object.entries(l10n)) {
                if (
                    locale === 'default' ||
                    isEqual(element?.locales?.[locale]?.[key], value)
                ) {
                    continue;
                }
                element.locales ??= {};
                element.locales[locale] ??= {};
                element.locales[locale][key] = value;
                updatedKeys.push([elementId, locale, key]);
            }
        }
    }
    return { result, updatedKeys, ignoredElements };
}

export default function useImportL10nAction() {
    const { state: form, set } = useFormContext();
    const mounted = useMounted();
    return useCallback(async () => {
        const toastId = toast(Builder.i18n.c('import-l10n.importing'), {
            autoClose: false,
        });

        let importedL10nData: FormL10n | null;

        try {
            importedL10nData = FormL10nSchema.parse(
                JSON.parse(
                    await openFile('L10n files', {
                        'application/json': ['.l10n.json'],
                    })
                )
            );
        } catch (e) {
            console.warn(e);
            importedL10nData = null;
        }

        if (!importedL10nData) {
            toast.dismiss(toastId);
            return;
        }

        if (!mounted) return;

        const { result, ignoredElements, updatedKeys } = applyL10nData(
            form,
            importedL10nData
        );

        set(result);

        const { c } = Builder.i18n;

        let message = c(
            `import-l10n.success.${updatedKeys.length === 1 ? 'singular' : 'plural'}`,
            {
                count: updatedKeys.length,
            }
        );

        if (ignoredElements.length > 0) {
            message = `${message} ${c(
                `import-l10n.unmatchedElementsMessage.${ignoredElements.length === 1 ? 'singular' : 'plural'}`,
                {
                    count: ignoredElements.length,
                }
            )}`;
        }

        toast.update(toastId, {
            autoClose: 3000,
            render: (
                <>
                    <CheckCircleFill className="me-2" /> {message}
                </>
            ),
        });
    }, [form, toast, set]);
}

export const ACTION_NAME = 'formEditor.form.importL10n';

setHotkey(ACTION_NAME, 'ctrl+alt+j,meta+alt+j');

export const useAction = useImportL10nAction;

export function useActionHotkey() {
    useHotkey(ACTION_NAME, useAction());
}
